import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Campaign as CampaignIcon,
  Visibility,
  VisibilityOff,
  CloudUpload,
  DeleteOutline,
  Image as ImageIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { DashboardService } from 'Dashboard/Domain/Services/DashboardService';

const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }
  if (url.startsWith('/api') && process.env.REACT_APP_BACKEND_URL) {
    const backendOrigin = process.env.REACT_APP_BACKEND_URL.replace(/\/api\/?$/, '');
    return `${backendOrigin}${url}`;
  }
  return url;
};

export default function AdminNewsView() {
  const [loading, setLoading] = useState(true);
  const [newsList, setNewsList] = useState([]);
  const [search, setSearch] = useState('');

  // Dialog state
  const [openModal, setOpenModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Form values
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    content: '',
    imageUrl: '',
    actionUrl: '',
    actionText: '',
    publishDate: '',
    expireDate: '',
    isVisible: true,
    position: 0,
  });

  const loadNews = async () => {
    try {
      setLoading(true);
      const res = await DashboardService.getAdminNewsList({ search });
      if (res?.data) {
        setNewsList(res.data);
      }
    } catch {
      toast.error('Error al cargar noticias.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNews();
  }, []);

  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormData({
      title: '',
      category: '',
      content: '',
      imageUrl: '',
      actionUrl: '',
      actionText: '',
      publishDate: new Date().toISOString().slice(0, 16),
      expireDate: '',
      isVisible: true,
      position: 0,
    });
    setOpenModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || '',
      category: item.category || '',
      content: item.content || '',
      imageUrl: item.imageUrl || '',
      actionUrl: item.actionUrl || '',
      actionText: item.actionText || '',
      publishDate: item.publishDate ? item.publishDate.replace(' ', 'T').slice(0, 16) : '',
      expireDate: item.expireDate ? item.expireDate.replace(' ', 'T').slice(0, 16) : '',
      isVisible: Boolean(item.isVisible),
      position: item.position || 0,
    });
    setOpenModal(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecciona un archivo de imagen válido (JPG, PNG, WEBP).');
      return;
    }

    try {
      setUploadingImage(true);
      const res = await DashboardService.uploadNewsImage(file);
      if (res?.imageUrl) {
        setFormData((prev) => ({ ...prev, imageUrl: res.imageUrl }));
        toast.success('Imagen subida correctamente.');
      }
    } catch {
      toast.error('Error al subir la imagen.');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('El título y contenido son obligatorios.');
      return;
    }

    try {
      setSaving(true);
      if (editingItem) {
        await DashboardService.updateNews(editingItem.id, formData);
        toast.success('Noticia actualizada.');
      } else {
        await DashboardService.createNews(formData);
        toast.success('Noticia creada.');
      }
      setOpenModal(false);
      loadNews();
    } catch {
      toast.error('Error al guardar la noticia.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta noticia?')) return;
    try {
      await DashboardService.deleteNews(id);
      toast.success('Noticia eliminada.');
      loadNews();
    } catch {
      toast.error('Error al eliminar noticia.');
    }
  };

  const handleToggleVisibility = async (item) => {
    try {
      await DashboardService.updateNews(item.id, { isVisible: !item.isVisible });
      toast.success(`Noticia ${!item.isVisible ? 'publicada' : 'ocultada'}.`);
      loadNews();
    } catch {
      toast.error('Error al cambiar visibilidad.');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '10px',
              bgcolor: '#257eb9',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CampaignIcon fontSize="medium" />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Noticias del Carrusel
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Crea y administra las noticias destacadas que se muestran en el carrusel de Inicio (máx. 5 visibles)
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreate}
          sx={{
            bgcolor: '#257eb9',
            '&:hover': { bgcolor: '#1d6594' },
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Nueva Noticia
        </Button>
      </Box>

      {/* Filter */}
      <Box sx={{ mb: 2.5, display: 'flex', gap: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar por título o contenido..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && loadNews()}
          sx={{ width: { xs: '100%', sm: '320px' } }}
        />
        <Button variant="outlined" onClick={loadNews} sx={{ textTransform: 'none' }}>
          Buscar
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: '#475569', width: 70 }}>Imagen</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Título</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Categoría</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Fecha Publicación</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Estado</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={32} sx={{ color: '#257eb9' }} />
                </TableCell>
              </TableRow>
            ) : newsList.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#64748b' }}>
                  No hay noticias registradas.
                </TableCell>
              </TableRow>
            ) : (
              newsList.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    {item.imageUrl ? (
                      <Box
                        component="img"
                        src={resolveImageUrl(item.imageUrl)}
                        alt={item.title}
                        sx={{
                          width: 48,
                          height: 36,
                          borderRadius: '6px',
                          objectFit: 'cover',
                          border: '1px solid #e2e8f0',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 48,
                          height: 36,
                          borderRadius: '6px',
                          bgcolor: '#f1f5f9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#94a3b8',
                        }}
                      >
                        <ImageIcon fontSize="small" />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1e293b', maxWidth: 260 }}>
                    {item.title}
                  </TableCell>
                  <TableCell sx={{ color: '#64748b' }}>{item.category || '-'}</TableCell>
                  <TableCell sx={{ color: '#64748b' }}>{item.publishDate || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={item.isVisible ? 'Visible' : 'Oculta'}
                      color={item.isVisible ? 'success' : 'default'}
                      variant={item.isVisible ? 'filled' : 'outlined'}
                      icon={item.isVisible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                      onClick={() => handleToggleVisibility(item)}
                      sx={{ cursor: 'pointer', fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenEdit(item)} sx={{ color: '#257eb9' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => handleDelete(item.id)} sx={{ color: '#ef4444' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal Create/Edit */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingItem ? 'Editar Noticia' : 'Crear Nueva Noticia'}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {/* Imagen de Cabecera (Parte superior de la noticia) */}
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: '10px',
                  bgcolor: '#f8fafc',
                  border: '1px dashed #cbd5e1',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
                  Imagen de Cabecera (Parte superior de la noticia)
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1.5 }}>
                  Esta imagen se mostrará en la parte superior de la noticia dentro del carrusel.
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={uploadingImage ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <CloudUpload />}
                    disabled={uploadingImage}
                    sx={{
                      bgcolor: '#257eb9',
                      '&:hover': { bgcolor: '#1d6594' },
                      textTransform: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                    }}
                  >
                    {uploadingImage ? 'Subiendo imagen...' : 'Subir imagen desde equipo'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleFileUpload}
                    />
                  </Button>

                  {formData.imageUrl && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteOutline />}
                      onClick={() => setFormData({ ...formData, imageUrl: '' })}
                      sx={{ textTransform: 'none', borderRadius: '8px' }}
                    >
                      Quitar imagen
                    </Button>
                  )}
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  label="O bien, introduce la URL de la imagen"
                  placeholder="https://... o ruta de imagen"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                  sx={{ mt: 2 }}
                />

                {formData.imageUrl && (
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      component="img"
                      src={resolveImageUrl(formData.imageUrl)}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                      sx={{
                        width: 160,
                        height: 90,
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '2px solid #e2e8f0',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      }}
                    />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                        Vista previa de la cabecera
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Así se verá la cabecera en el carrusel de inicio.
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Título de la Noticia"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Categoría / Subtítulo"
                placeholder="Ej: ESPACIO DE RECONOCIMIENTOS"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Contenido"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="URL de Enlace (Opcional)"
                placeholder="https://..."
                value={formData.actionUrl}
                onChange={(e) => setFormData({ ...formData, actionUrl: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Texto del Enlace"
                placeholder="Ej: Saber más"
                value={formData.actionText}
                onChange={(e) => setFormData({ ...formData, actionText: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Fecha de Publicación"
                InputLabelProps={{ shrink: true }}
                value={formData.publishDate}
                onChange={(e) => setFormData({ ...formData, publishDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Fecha de Caducidad (Opcional)"
                InputLabelProps={{ shrink: true }}
                value={formData.expireDate}
                onChange={(e) => setFormData({ ...formData, expireDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isVisible}
                    onChange={(e) => setFormData({ ...formData, isVisible: e.target.checked })}
                    color="primary"
                  />
                }
                label="Publicar inmediatamente (Visible en carrusel)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenModal(false)} sx={{ color: '#64748b' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              bgcolor: '#257eb9',
              '&:hover': { bgcolor: '#1d6594' },
              borderRadius: '8px',
              px: 3,
            }}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
